import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { 
  FaChartLine, 
  FaGlobe, 
  FaChevronDown,
  FaUser,
  FaSignOutAlt,
  FaCrown,
  FaInfoCircle,
  FaTags
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import './Header.css';
import config from '../../config';

const Header = () => {
  const { t } = useTranslation(['common']);
  const { language, changeLanguage, supportedLanguages } = useLanguage();
  const { isAuthenticated, user, loading } = useAuth();
  const navigate = useNavigate();
  
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const languageDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);
  const mobileDropdownRef = useRef(null);

  const toggleLanguageMenu = () => {
    setIsLanguageMenuOpen(!isLanguageMenuOpen);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLogout = async () => {
    navigate('/logout')
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setIsLanguageMenuOpen(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleTouch = (event) => {
      handleClickOutside(event);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleTouch);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleTouch);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsUserMenuOpen(false);
      setIsLanguageMenuOpen(false);
      setIsMobileMenuOpen(false);
    }
  }, [isAuthenticated]);

  const languageNames = {
    en: 'English',
    ru: 'Русский'
  };

  const handleLanguageChange = (lang) => {
    changeLanguage(lang);
    setIsLanguageMenuOpen(false);
  };

  const getUserInitials = () => {
    if (!user) return 'U';

    if (user.name) {
      const nameParts = user.name.split(' ');
      if (nameParts.length >= 2) {
        return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
      }
      return user.name[0].toUpperCase();
    }

    if (user.email) {
      return user.email[0].toUpperCase();
    }

    return 'U';
  };
  
  const isImageUrl = (url) => {
    if (!url || typeof url !== 'string') return false;

    return (
      url.startsWith('http') ||
      url.startsWith('data:image/')
    );
  };

  const getUserDisplayName = () => {
    if (user?.name) return user.name;
    if (user?.email) return user.email;
    return t('common.user');
  };

  const handleMobileMenuClick = (action) => {
    setIsMobileMenuOpen(false);
    if (action) {
      action();
    }
  };

  return (
    <>
      <header className="header">
        <div className="header__container">
          <div className="header__logo">
            <Link to="/" className="header__logo-link">
              <FaChartLine className="header__logo-icon" style={{ color: '#ff0000' }} />
              <span className="header__logo-text">{t('app.name')}</span>
            </Link>
          </div>
          <nav className="header__nav">
            <div style={{ flex: 1 }}></div>
            
            <div className="header__actions">
              <NavLink
                to="/about"
                className={({ isActive }) =>
                  `header__nav-link ${isActive ? 'header__nav-link--active' : ''}`
                }
              >
                {t('navigation.about')}
              </NavLink>

              <NavLink
                to="/pricing"
                className={({ isActive }) =>
                  `header__nav-link ${isActive ? 'header__nav-link--active' : ''}`
                }
              >
                {t('navigation.pricing')}
              </NavLink>
              
              { loading ? (
                <div className="header__nav-item">
                  <div 
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: '#e2e8f0',
                      animation: 'pulse 1.5s infinite',
                    }}
                  />
                </div>
                ) : !isAuthenticated ? (
                <>
                  <NavLink
                    to="/login"
                    className={({ isActive }) =>
                      `header__nav-link ${isActive ? 'header__nav-link--active' : ''}`
                    }
                  >
                    {t('auth.login.title')}
                  </NavLink>

                  <NavLink
                    to="/register"
                    className="btn btn-primary"
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      backgroundColor: '#ff0000',
                      color: 'white',
                      fontWeight: '500',
                      fontSize: '0.875rem'
                    }}
                  >
                    {t('auth.register.title')}
                  </NavLink>
                </>
              ) : (
                <>
                <div className="header__subscription">
                  <span className="header__subscription-label">
                    {t(`subscription.${user.subscription_type}`)}
                  </span>
                </div>

                <div className="header__nav-item" ref={userDropdownRef}>
                  <div className={`header__dropdown ${isUserMenuOpen ? 'header__dropdown--open' : ''}`}>
                    <button 
                      className="header__dropdown-toggle"
                      onClick={toggleUserMenu}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <div 
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#ff0000',
                          color: 'white',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                        }}
                      >
                        {isImageUrl(user?.picture) ? (
                          <img
                            src={user.picture}
                            alt="Avatar"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          getUserInitials()
                        )}
                      </div>
                      <FaChevronDown className="header__dropdown-icon" />
                    </button>
                    
                    {isUserMenuOpen && (
                      <div className="header__dropdown-menu">
                        <NavLink 
                          to={config.VITE_HOME_ROUTE}
                          className="header__dropdown-item"
                          onClick={() => {
                            setIsUserMenuOpen(false);
                          }}
                        >
                          <FaChartLine className="header__dropdown-item-icon" />
                          {t('navigation.home')}
                        </NavLink>
                        
                        <NavLink 
                          to="/profile" 
                          className="header__dropdown-item"
                          onClick={() => {
                            setIsUserMenuOpen(false);
                          }}
                        >
                          <FaUser className="header__dropdown-item-icon" />
                          {t('navigation.profile')}
                        </NavLink>

                        <div className="header__dropdown-item header__dropdown-subscription">
                          <FaCrown className="header__dropdown-item-icon" />
                          <div>
                            <div className="header__dropdown-subscription-plan">
                              {t(`subscription.${user.subscription_type}`) || 'Unknown'}
                            </div>
                            <div className="header__dropdown-subscription-status">
                              {t(`subscription.${user.subscription_status}`) || 'Unknown'}
                            </div>
                          </div>
                        </div>

                        <hr style={{ margin: '0.5rem 0', border: 'none', borderTop: '1px solid #f7fafc' }} />
                        
                        <button 
                          className="header__dropdown-item"
                          onClick={() => {
                            handleLogout();
                          }}
                          style={{
                            color: '#e53e3e',
                            width: '100%',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left'
                          }}
                        >
                          <FaSignOutAlt className="header__dropdown-item-icon" />
                          {t('auth.logout')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                </>
              )}

              <div className="header__nav-item" ref={languageDropdownRef}>
                <div className={`header__dropdown ${isLanguageMenuOpen ? 'header__dropdown--open' : ''}`}>
                  <button 
                    className="header__dropdown-toggle"
                    onClick={toggleLanguageMenu}
                  >
                    <FaGlobe style={{ marginRight: '0.5rem' }} />
                    {languageNames[language]}
                    <FaChevronDown className="header__dropdown-icon" />
                  </button>
                  
                  {isLanguageMenuOpen && (
                    <div className="header__dropdown-menu header__dropdown-menu--left">
                      {supportedLanguages.map((lang) => (
                        <button
                          key={lang}
                          className={`header__dropdown-item ${
                            lang === language ? 'header__language-option--active' : ''
                          }`}
                          onClick={() => handleLanguageChange(lang)}
                        >
                          {languageNames[lang]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="header__mobile-wrapper" ref={mobileDropdownRef}>
              <div className={`header__dropdown ${isMobileMenuOpen ? 'header__dropdown--open' : ''}`}>
                <button 
                  onClick={toggleMobileMenu}
                  className="header__dropdown-toggle"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.5rem',
                    color: 'var(--color-text-secondary)'
                  }}
                >
                  {user ? (
                    <div 
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#ff0000',
                        color: 'white',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                      }}
                    >
                      {isImageUrl(user?.picture) ? (
                        <img
                          src={user.picture}
                          alt="Avatar"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        getUserInitials()
                      )}
                    </div>
                  ) : (
                    <FaUser style={{
                      width: '32px',
                      height: '32px',
                      padding: '8px',
                      borderRadius: '50%',
                      backgroundColor: '#f1f5f9',
                      color: '#64748b'
                    }} />
                  )}
                  <FaChevronDown className="header__dropdown-icon" />
                </button>
                
                {isMobileMenuOpen && (
                  <div className="header__dropdown-menu" style={{ position: 'absolute', top: '100%', right: '0', left: 'auto' }}>
                    {user ? (
                      <>
                        <button 
                          className="header__dropdown-item"
                          onClick={() => handleMobileMenuClick(() => navigate(config.VITE_HOME_ROUTE))}
                          style={{
                            width: '100%',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left',
                            cursor: 'pointer'
                          }}
                        >
                          <FaChartLine className="header__dropdown-item-icon" />
                          {t('navigation.home')}
                        </button>
                        
                        <button 
                          className="header__dropdown-item"
                          onClick={() => handleMobileMenuClick(() => navigate('/profile'))}
                          style={{
                            width: '100%',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left',
                            cursor: 'pointer'
                          }}
                        >
                          <FaUser className="header__dropdown-item-icon" />
                          {t('navigation.profile')}
                        </button>

                        <button 
                          className="header__dropdown-item"
                          onClick={() => handleMobileMenuClick(() => navigate('/about'))}
                          style={{
                            width: '100%',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left',
                            cursor: 'pointer'
                          }}
                        >
                          <FaInfoCircle className="header__dropdown-item-icon" />
                          {t('navigation.about')}
                        </button>

                        <button 
                          className="header__dropdown-item"
                          onClick={() => handleMobileMenuClick(() => navigate('/pricing'))}
                          style={{
                            width: '100%',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left',
                            cursor: 'pointer'
                          }}
                        >
                          <FaTags className="header__dropdown-item-icon" />
                          {t('navigation.pricing')}
                        </button>

                        <div className="header__dropdown-item header__dropdown-subscription">
                          <FaCrown className="header__dropdown-item-icon" />
                          <div>
                            <div className="header__dropdown-subscription-plan">
                              {t(`subscription.${user.subscription_type}`) || 'Unknown'}
                            </div>
                            <div className="header__dropdown-subscription-status">
                              {t(`subscription.${user.subscription_status}`) || 'Unknown'}
                            </div>
                          </div>
                        </div>

                        <div className="header__dropdown-item" style={{ padding: '0.75rem 1.25rem' }}>
                          <FaGlobe className="header__dropdown-item-icon" />
                          <select 
                            value={language} 
                            onChange={(e) => {
                              changeLanguage(e.target.value);
                              setIsMobileMenuOpen(false);
                            }}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: 'var(--color-text-primary)',
                              fontSize: '0.9375rem',
                              fontWeight: '500',
                              cursor: 'pointer'
                            }}
                          >
                            {supportedLanguages.map((lang) => (
                              <option key={lang} value={lang}>
                                {languageNames[lang]}
                              </option>
                            ))}
                          </select>
                        </div>

                        <hr style={{ margin: '0.5rem 0', border: 'none', borderTop: '1px solid #f7fafc' }} />
                        
                        <button 
                          className="header__dropdown-item"
                          onClick={() => handleMobileMenuClick(() => handleLogout())}
                          style={{
                            color: '#e53e3e',
                            width: '100%',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left',
                            cursor: 'pointer'
                          }}
                        >
                          <FaSignOutAlt className="header__dropdown-item-icon" />
                          {t('auth.logout')}
                        </button>
                      </>
                    ) : (
                      <>
                        <button 
                          className="header__dropdown-item"
                          onClick={() => handleMobileMenuClick(() => navigate('/about'))}
                          style={{
                            width: '100%',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left',
                            cursor: 'pointer'
                          }}
                        >
                          <FaInfoCircle className="header__dropdown-item-icon" />
                          {t('navigation.about')}
                        </button>

                        <button 
                          className="header__dropdown-item"
                          onClick={() => handleMobileMenuClick(() => navigate('/pricing'))}
                          style={{
                            width: '100%',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left',
                            cursor: 'pointer'
                          }}
                        >
                          <FaTags className="header__dropdown-item-icon" />
                          {t('navigation.pricing')}
                        </button>

                        <div className="header__dropdown-item" style={{ padding: '0.75rem 1.25rem' }}>
                          <FaGlobe className="header__dropdown-item-icon" />
                          <select 
                            value={language} 
                            onChange={(e) => {
                              changeLanguage(e.target.value);
                              setIsMobileMenuOpen(false);
                            }}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: 'var(--color-text-primary)',
                              fontSize: '0.9375rem',
                              fontWeight: '500',
                              cursor: 'pointer'
                            }}
                          >
                            {supportedLanguages.map((lang) => (
                              <option key={lang} value={lang}>
                                {languageNames[lang]}
                              </option>
                            ))}
                          </select>
                        </div>

                        <hr style={{ margin: '0.5rem 0', border: 'none', borderTop: '1px solid #f7fafc' }} />

                        <button 
                          className="header__dropdown-item"
                          onClick={() => handleMobileMenuClick(() => navigate('/login'))}
                          style={{
                            fontWeight: '600',
                            width: '100%',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left',
                            cursor: 'pointer'
                          }}
                        >
                          <FaUser className="header__dropdown-item-icon" />
                          {t('auth.login.title')}
                        </button>

                        <button 
                          className="header__dropdown-item"
                          onClick={() => handleMobileMenuClick(() => navigate('/register'))}
                          style={{ 
                            backgroundColor: 'rgba(255, 0, 0, 0.1)',
                            color: '#ff0000',
                            fontWeight: '600',
                            width: '100%',
                            border: 'none',
                            textAlign: 'left',
                            cursor: 'pointer'
                          }}
                        >
                          <FaUser className="header__dropdown-item-icon" />
                          {t('auth.register.title')}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </nav>
        </div>
      </header>
    </>
  );
};

export default Header;